import { useEffect, useMemo, useState } from 'react';
import { adminApi, downloadBlob } from '@/lib/api';
import { Download, Loader2, Plus, Shuffle, SlidersHorizontal, Warehouse as WarehouseIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import { toast } from 'sonner';

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export default function RawStockPage() {
  const [loading, setLoading] = useState(true);
  const [lotsLoading, setLotsLoading] = useState(false);
  const [warehouses, setWarehouses] = useState([]);
  const [rawItems, setRawItems] = useState([]);
  const [selectedWarehouseId, setSelectedWarehouseId] = useState('');
  const [rawLots, setRawLots] = useState([]);
  const [packingStockRows, setPackingStockRows] = useState([]);
  const [packingConsumptionRows, setPackingConsumptionRows] = useState([]);
  const [activePackingTab, setActivePackingTab] = useState('current-stock');
  const [saving, setSaving] = useState(false);
  const [showReceiveModal, setShowReceiveModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [rawStockRefPreview, setRawStockRefPreview] = useState('');
  const [rawSuggestions, setRawSuggestions] = useState([]);
  const [receiveForm, setReceiveForm] = useState({
    warehouse_id: '',
    raw_item_id: '',
    bags_count: '',
    kg_per_bag: '',
    received_at: '',
    note: '',
  });
  const [transferForm, setTransferForm] = useState({
    from_warehouse_id: '',
    to_warehouse_id: '',
    raw_item_id: '',
    kg_required: '',
    note: '',
  });
  const [adjustForm, setAdjustForm] = useState({
    warehouse_id: '',
    raw_item_id: '',
    kg_adjustment: '',
    bags_adjustment: '',
    kg_per_bag: '',
    note: '',
  });
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const totalPages = Math.max(1, Math.ceil(rawLots.length / pageSize));

  const pagedRawLots = useMemo(() => {
    const start = (page - 1) * pageSize;
    return rawLots.slice(start, start + pageSize);
  }, [rawLots, page]);

  const selectedWarehouse = useMemo(
    () => warehouses.find((warehouse) => warehouse.id === selectedWarehouseId),
    [warehouses, selectedWarehouseId]
  );

  const isPackingWarehouse = useMemo(() => {
    if (!selectedWarehouse) return false;
    const name = (selectedWarehouse.name || '').toLowerCase();
    return selectedWarehouse.tag === 'PACKING' || name.includes('packing');
  }, [selectedWarehouse]);

  useEffect(() => {
    const fetchInitial = async () => {
      setLoading(true);
      try {
        const [warehousesRes, rawItemsRes] = await Promise.all([adminApi.getWarehouses(), adminApi.getRawItems()]);
        const allWarehouses = warehousesRes.data || [];
        const filtered = allWarehouses.filter((w) => {
          const name = (w.name || '').toLowerCase();
          return name.includes('main') || name.includes('packing') || w.tag === 'MAIN' || w.tag === 'PACKING';
        });
        setWarehouses(filtered);
        setRawItems(rawItemsRes.data || []);
        if (filtered.length > 0) {
          setSelectedWarehouseId(filtered[0].id);
          const packingWarehouse = filtered.find((w) => (w.name || '').toLowerCase().includes('packing') || w.tag === 'PACKING');
          setReceiveForm((prev) => ({ ...prev, warehouse_id: filtered[0].id }));
          setTransferForm((prev) => ({
            ...prev,
            from_warehouse_id: filtered[0].id,
            to_warehouse_id: packingWarehouse?.id || prev.to_warehouse_id,
          }));
          setAdjustForm((prev) => ({ ...prev, warehouse_id: filtered[0].id }));
        }
      } catch (error) {
        toast.error('Failed to load warehouses');
      } finally {
        setLoading(false);
      }
    };

    fetchInitial();
  }, []);

  useEffect(() => {
    if (!isPackingWarehouse) {
      setActivePackingTab('current-stock');
    }
  }, [isPackingWarehouse]);

  useEffect(() => {
    if (!selectedWarehouseId) return;
    const fetchLots = async () => {
      setLotsLoading(true);
      try {
        if (isPackingWarehouse) {
          const [lotsRes, movementRes] = await Promise.all([
            adminApi.getRawStockLots({ warehouse_id: selectedWarehouseId }),
            adminApi.getRawStockMovements({ warehouse_id: selectedWarehouseId }),
          ]);

          const lots = lotsRes.data || [];
          const stockByItem = new Map();
          lots.forEach((lot) => {
            const rawItemId = lot.raw_item_id;
            if (!rawItemId) return;
            if (!stockByItem.has(rawItemId)) {
              stockByItem.set(rawItemId, {
                raw_item_id: rawItemId,
                raw_item_name: lot.raw_item_name || 'Unknown',
                kg_total: 0,
                bags_count: 0,
              });
            }
            const row = stockByItem.get(rawItemId);
            row.kg_total += Number(lot.kg_total || 0);
            row.bags_count += Number(lot.bags_count || 0);
          });

          const currentRows = Array.from(stockByItem.values())
            .map((row) => ({
              ...row,
              kg_total: Number(row.kg_total || 0),
              bags_count: Number(row.bags_count || 0),
              kg_per_bag: row.bags_count > 0 ? row.kg_total / row.bags_count : 0,
            }))
            .filter((row) => row.kg_total > 0.000001)
            .sort((a, b) => a.raw_item_name.localeCompare(b.raw_item_name));

          const movements = movementRes.data || [];
          const consumptionByItem = new Map();
          movements.forEach((movement) => {
            const rawItemId = movement.raw_item_id;
            if (!rawItemId) return;
            if (!consumptionByItem.has(rawItemId)) {
              consumptionByItem.set(rawItemId, {
                raw_item_id: rawItemId,
                raw_item_name: movement.raw_item_name || 'Unknown',
                kg_total: 0,
                bags_count: 0,
              });
            }
            const row = consumptionByItem.get(rawItemId);
            row.kg_total += Number(movement.kg || 0);
            row.bags_count += Number(movement.bags || 0);
          });

          const consumptionRows = Array.from(consumptionByItem.values())
            .map((row) => ({
              ...row,
              kg_total: Number(row.kg_total || 0),
              bags_count: Number(row.bags_count || 0),
              kg_per_bag: row.bags_count > 0 ? row.kg_total / row.bags_count : 0,
            }))
            .filter((row) => row.kg_total > 0.000001)
            .sort((a, b) => a.raw_item_name.localeCompare(b.raw_item_name));

          setPackingStockRows(currentRows);
          setPackingConsumptionRows(consumptionRows);
          setRawLots([]);
        } else {
          const response = await adminApi.getRawStockLots({ warehouse_id: selectedWarehouseId });
          setRawLots(response.data || []);
          setPackingStockRows([]);
          setPackingConsumptionRows([]);
        }
        setPage(1);
      } catch (error) {
        toast.error('Failed to load raw stock lots');
      } finally {
        setLotsLoading(false);
      }
    };
    fetchLots();
  }, [selectedWarehouseId, isPackingWarehouse]);

  useEffect(() => {
    if (!showReceiveModal) return;
    adminApi.getNextRawStockRef()
      .then((response) => setRawStockRefPreview(response.data?.stock_ref_no || ''))
      .catch(() => setRawStockRefPreview(''));
  }, [showReceiveModal]);

  useEffect(() => {
    if (!transferForm.from_warehouse_id || !transferForm.raw_item_id || !transferForm.kg_required) {
      setRawSuggestions([]);
      return;
    }
    adminApi.suggestRawStock({
      warehouse_id: transferForm.from_warehouse_id,
      raw_item_id: transferForm.raw_item_id,
      kg_required: transferForm.kg_required,
    })
      .then((response) => setRawSuggestions(response.data?.suggestions || []))
      .catch(() => setRawSuggestions([]));
  }, [transferForm.from_warehouse_id, transferForm.raw_item_id, transferForm.kg_required]);

  const warehouseOptions = useMemo(() => warehouses.filter((warehouse) => warehouse.is_active !== false), [warehouses]);
  const rawItemOptions = useMemo(() => rawItems.filter((item) => item.is_active !== false), [rawItems]);

  const downloadExcel = async () => {
    try {
      const res = await adminApi.downloadAdminDatasetExcel('raw-stock', {
        warehouse_id: selectedWarehouseId || undefined,
      });
      downloadBlob(res.data, `raw-stock.xlsx`);
    } catch {
      toast.error('Failed to download Excel');
    }
  };

  const refreshLots = async () => {
    if (!selectedWarehouseId) return;
    setLotsLoading(true);
    try {
      if (isPackingWarehouse) {
        const [lotsRes, movementRes] = await Promise.all([
          adminApi.getRawStockLots({ warehouse_id: selectedWarehouseId }),
          adminApi.getRawStockMovements({ warehouse_id: selectedWarehouseId }),
        ]);
        const lots = lotsRes.data || [];
        const stockByItem = new Map();
        lots.forEach((lot) => {
          const rawItemId = lot.raw_item_id;
          if (!rawItemId) return;
          const existing = stockByItem.get(rawItemId) || { raw_item_id: rawItemId, raw_item_name: lot.raw_item_name || 'Unknown', kg_total: 0, bags_count: 0 };
          existing.kg_total += Number(lot.kg_total || 0);
          existing.bags_count += Number(lot.bags_count || 0);
          stockByItem.set(rawItemId, existing);
        });
        setPackingStockRows(Array.from(stockByItem.values()).map((row) => ({
          ...row,
          kg_total: Number(row.kg_total || 0),
          bags_count: Number(row.bags_count || 0),
          kg_per_bag: row.bags_count > 0 ? row.kg_total / row.bags_count : 0,
        })).filter((row) => row.kg_total > 0.000001).sort((a, b) => a.raw_item_name.localeCompare(b.raw_item_name)));

        const movements = movementRes.data || [];
        const consumptionByItem = new Map();
        movements.filter((movement) => ['consume', 'adjustment'].includes(String(movement.type || '').toLowerCase()) && Number(movement.kg || 0) < 0)
          .forEach((movement) => {
            const rawItemId = movement.raw_item_id;
            if (!rawItemId) return;
            const existing = consumptionByItem.get(rawItemId) || { raw_item_id: rawItemId, raw_item_name: movement.raw_item_name || 'Unknown', kg_total: 0, bags_count: 0 };
            existing.kg_total += Math.abs(Number(movement.kg || 0));
            existing.bags_count += Math.abs(Number(movement.bags || 0));
            consumptionByItem.set(rawItemId, existing);
          });
        setPackingConsumptionRows(Array.from(consumptionByItem.values()).map((row) => ({
          ...row,
          kg_total: Number(row.kg_total || 0),
          bags_count: Number(row.bags_count || 0),
          kg_per_bag: row.bags_count > 0 ? row.kg_total / row.bags_count : 0,
        })).filter((row) => row.kg_total > 0.000001).sort((a, b) => a.raw_item_name.localeCompare(b.raw_item_name)));
        setRawLots([]);
      } else {
        const response = await adminApi.getRawStockLots({ warehouse_id: selectedWarehouseId });
        setRawLots(response.data || []);
        setPackingStockRows([]);
        setPackingConsumptionRows([]);
      }
      setPage(1);
    } catch {
      toast.error('Failed to load raw stock lots');
    } finally {
      setLotsLoading(false);
    }
  };

  const handleReceiveRawStock = async () => {
    if (!receiveForm.warehouse_id || !receiveForm.raw_item_id || !receiveForm.bags_count || !receiveForm.kg_per_bag) {
      toast.error('Select warehouse, raw item, bags, and kg per bag');
      return;
    }
    setSaving(true);
    try {
      await adminApi.receiveRawStock({
        warehouse_id: receiveForm.warehouse_id,
        raw_item_id: receiveForm.raw_item_id,
        bags_count: parseInt(receiveForm.bags_count, 10),
        kg_per_bag: parseFloat(receiveForm.kg_per_bag),
        received_at: receiveForm.received_at || undefined,
        note: receiveForm.note || undefined,
      });
      toast.success('Raw stock received');
      setShowReceiveModal(false);
      await refreshLots();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to receive raw stock');
    } finally {
      setSaving(false);
    }
  };

  const handleTransferRawStock = async () => {
    if (!transferForm.from_warehouse_id || !transferForm.to_warehouse_id || !transferForm.raw_item_id || !transferForm.kg_required) {
      toast.error('Select warehouses, raw item, and required kg');
      return;
    }
    setSaving(true);
    try {
      await adminApi.transferRawStock({
        from_warehouse_id: transferForm.from_warehouse_id,
        to_warehouse_id: transferForm.to_warehouse_id,
        raw_item_id: transferForm.raw_item_id,
        kg_required: parseFloat(transferForm.kg_required),
        note: transferForm.note || undefined,
      });
      toast.success('Raw stock transferred');
      setShowTransferModal(false);
      await refreshLots();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to transfer raw stock');
    } finally {
      setSaving(false);
    }
  };

  const handleAdjustRawStock = async () => {
    if (!adjustForm.warehouse_id || !adjustForm.raw_item_id || !adjustForm.kg_adjustment) {
      toast.error('Select warehouse, raw item, and adjustment kg');
      return;
    }
    setSaving(true);
    try {
      await adminApi.adjustRawStock({
        warehouse_id: adjustForm.warehouse_id,
        raw_item_id: adjustForm.raw_item_id,
        kg_adjustment: parseFloat(adjustForm.kg_adjustment),
        bags_adjustment: adjustForm.bags_adjustment ? parseInt(adjustForm.bags_adjustment, 10) : undefined,
        kg_per_bag: adjustForm.kg_per_bag ? parseFloat(adjustForm.kg_per_bag) : undefined,
        note: adjustForm.note || undefined,
      });
      toast.success('Raw stock adjusted');
      setShowAdjustModal(false);
      await refreshLots();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to adjust raw stock');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="raw-stock-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#2D2420]">Raw Stock</h1>
          <p className="text-[#A8A8A8] mt-1">View raw stock availability for Main and Packing warehouses</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="border-[#E5E5E5]" onClick={downloadExcel}>
            <Download className="w-4 h-4 mr-2" />
            Download Excel
          </Button>
          <Button variant="outline" className="border-[#E5E5E5]" onClick={() => setShowAdjustModal(true)}>
            <SlidersHorizontal className="w-4 h-4 mr-2" />
            Adjust Stock
          </Button>
          <Button variant="outline" className="border-[#E5E5E5]" onClick={() => setShowTransferModal(true)}>
            <Shuffle className="w-4 h-4 mr-2" />
            Transfer Stock
          </Button>
          <Button className="bg-[#C97A5E] hover:bg-[#B06850]" onClick={() => setShowReceiveModal(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Receive Raw Stock
          </Button>
        </div>
      </div>

      <Card className="bg-white border-[#E5E5E5]">
        <CardContent className="p-4 space-y-4">
          <div className="w-full lg:w-80">
            <Label>Warehouse</Label>
            <Select value={selectedWarehouseId} onValueChange={setSelectedWarehouseId}>
              <SelectTrigger className="border-[#E5E5E5]">
                <SelectValue placeholder="Select warehouse" />
              </SelectTrigger>
              <SelectContent>
                {warehouses.map((warehouse) => (
                  <SelectItem key={warehouse.id} value={warehouse.id}>{warehouse.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {loading || lotsLoading ? (
            <div className="flex items-center justify-center h-40">
              <Loader2 className="w-6 h-6 animate-spin text-[#C97A5E]" />
            </div>
          ) : (
            <>
              {isPackingWarehouse && (
                <Tabs value={activePackingTab} onValueChange={setActivePackingTab}>
                  <TabsList className="grid w-full max-w-md grid-cols-2">
                    <TabsTrigger value="current-stock">Raw Stock Details</TabsTrigger>
                    <TabsTrigger value="total-consumption">Total consumption</TabsTrigger>
                  </TabsList>
                </Tabs>
              )}

              {(isPackingWarehouse ? (activePackingTab === 'current-stock' ? packingStockRows.length === 0 : packingConsumptionRows.length === 0) : rawLots.length === 0) ? (
                <div className="flex items-center justify-center h-40 text-[#A8A8A8]">
                  <WarehouseIcon className="w-6 h-6 mr-2" />
                  {isPackingWarehouse && activePackingTab === 'total-consumption' ? 'No consumption data yet' : 'No raw stock lots yet'}
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-[#FAFAF9] border-b border-[#E5E5E5]">
                        <tr>
                          {!isPackingWarehouse && <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Stock Ref</th>}
                          <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Raw Item</th>
                          <th className="text-right py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Bags</th>
                          <th className="text-right py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Total KG</th>
                          <th className="text-right py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">KG/Bag</th>
                          {!isPackingWarehouse && <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Received</th>}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#E5E5E5]">
                        {isPackingWarehouse
                          ? (activePackingTab === 'current-stock' ? packingStockRows : packingConsumptionRows).map((row) => (
                              <tr key={row.raw_item_id} className="hover:bg-[#FAFAF9]/60">
                                <td className="py-3 px-4 text-[#2D2420]">{row.raw_item_name}</td>
                                <td className="py-3 px-4 text-right text-[#2D2420]">{row.bags_count.toFixed(2)}</td>
                                <td className="py-3 px-4 text-right text-[#2D2420]">{row.kg_total.toFixed(3)}</td>
                                <td className="py-3 px-4 text-right text-[#A8A8A8]">{row.kg_per_bag?.toFixed(2)}</td>
                              </tr>
                            ))
                          : pagedRawLots.map((lot) => (
                              <tr key={lot.id} className="hover:bg-[#FAFAF9]/60">
                                <td className="py-3 px-4 text-[#2D2420] font-medium">{lot.stock_ref_no}</td>
                                <td className="py-3 px-4 text-[#2D2420]">{lot.raw_item_name}</td>
                                <td className="py-3 px-4 text-right text-[#2D2420]">{lot.bags_count}</td>
                                <td className="py-3 px-4 text-right text-[#2D2420]">{lot.kg_total}</td>
                                <td className="py-3 px-4 text-right text-[#A8A8A8]">{lot.kg_per_bag?.toFixed(2)}</td>
                                <td className="py-3 px-4 text-[#A8A8A8]">{formatDateTime(lot.received_at)}</td>
                              </tr>
                            ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 pt-2">
                    <p className="text-sm text-[#A8A8A8]">
                      {isPackingWarehouse
                        ? `Showing 1 to ${(activePackingTab === 'current-stock' ? packingStockRows : packingConsumptionRows).length} of ${(activePackingTab === 'current-stock' ? packingStockRows : packingConsumptionRows).length}`
                        : `Showing ${(page - 1) * pageSize + 1} to ${Math.min(page * pageSize, rawLots.length)} of ${rawLots.length}`}
                    </p>
                    {!isPackingWarehouse && (
                      <Pagination>
                        <PaginationContent>
                          <PaginationItem>
                            <PaginationPrevious
                              onClick={(e) => {
                                e.preventDefault();
                                setPage((prev) => Math.max(1, prev - 1));
                              }}
                              className={page === 1 ? 'pointer-events-none opacity-50' : ''}
                            />
                          </PaginationItem>
                          <PaginationItem>
                            <span className="px-3 text-sm text-[#5C4033]">Page {page} of {totalPages}</span>
                          </PaginationItem>
                          <PaginationItem>
                            <PaginationNext
                              onClick={(e) => {
                                e.preventDefault();
                                setPage((prev) => Math.min(totalPages, prev + 1));
                              }}
                              className={page >= totalPages ? 'pointer-events-none opacity-50' : ''}
                            />
                          </PaginationItem>
                        </PaginationContent>
                      </Pagination>
                    )}
                  </div>
                </>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={showReceiveModal} onOpenChange={setShowReceiveModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[#2D2420]">Receive Raw Stock</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Warehouse</Label>
              <Select value={receiveForm.warehouse_id} onValueChange={(value) => setReceiveForm((prev) => ({ ...prev, warehouse_id: value }))}>
                <SelectTrigger className="border-[#E5E5E5]"><SelectValue placeholder="Select warehouse" /></SelectTrigger>
                <SelectContent>{warehouseOptions.map((warehouse) => <SelectItem key={warehouse.id} value={warehouse.id}>{warehouse.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Raw Item</Label>
              <Select value={receiveForm.raw_item_id} onValueChange={(value) => setReceiveForm((prev) => ({ ...prev, raw_item_id: value }))}>
                <SelectTrigger className="border-[#E5E5E5]"><SelectValue placeholder="Select raw item" /></SelectTrigger>
                <SelectContent>{rawItemOptions.map((item) => <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Stock Ref No</Label>
              <Input value={rawStockRefPreview || 'Generating...'} disabled className="border-[#E5E5E5]" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Bags</Label><Input type="number" value={receiveForm.bags_count} onChange={(e) => setReceiveForm((prev) => ({ ...prev, bags_count: e.target.value }))} className="border-[#E5E5E5]" /></div>
              <div><Label>KG per Bag</Label><Input type="number" value={receiveForm.kg_per_bag} onChange={(e) => setReceiveForm((prev) => ({ ...prev, kg_per_bag: e.target.value }))} className="border-[#E5E5E5]" /></div>
            </div>
            <div>
              <Label>Received Date</Label>
              <Input type="date" value={receiveForm.received_at} onChange={(e) => setReceiveForm((prev) => ({ ...prev, received_at: e.target.value }))} className="border-[#E5E5E5]" />
            </div>
            <div>
              <Label>Note</Label>
              <Textarea value={receiveForm.note} onChange={(e) => setReceiveForm((prev) => ({ ...prev, note: e.target.value }))} className="border-[#E5E5E5]" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReceiveModal(false)}>Cancel</Button>
            <Button onClick={handleReceiveRawStock} disabled={saving}>{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Receive'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showTransferModal} onOpenChange={setShowTransferModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[#2D2420]">Transfer Raw Stock</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>From Warehouse</Label>
              <Select value={transferForm.from_warehouse_id} onValueChange={(value) => setTransferForm((prev) => ({ ...prev, from_warehouse_id: value }))}>
                <SelectTrigger className="border-[#E5E5E5]"><SelectValue placeholder="Select source" /></SelectTrigger>
                <SelectContent>{warehouseOptions.map((warehouse) => <SelectItem key={warehouse.id} value={warehouse.id}>{warehouse.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>To Warehouse</Label>
              <Select value={transferForm.to_warehouse_id} onValueChange={(value) => setTransferForm((prev) => ({ ...prev, to_warehouse_id: value }))}>
                <SelectTrigger className="border-[#E5E5E5]"><SelectValue placeholder="Select destination" /></SelectTrigger>
                <SelectContent>{warehouseOptions.map((warehouse) => <SelectItem key={warehouse.id} value={warehouse.id}>{warehouse.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Raw Item</Label>
              <Select value={transferForm.raw_item_id} onValueChange={(value) => setTransferForm((prev) => ({ ...prev, raw_item_id: value }))}>
                <SelectTrigger className="border-[#E5E5E5]"><SelectValue placeholder="Select raw item" /></SelectTrigger>
                <SelectContent>{rawItemOptions.map((item) => <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Required KG</Label>
              <Input type="number" step="0.001" value={transferForm.kg_required} onChange={(e) => setTransferForm((prev) => ({ ...prev, kg_required: e.target.value }))} className="border-[#E5E5E5]" />
            </div>
            {rawSuggestions.length > 0 ? (
              <div className="rounded-lg border border-[#E5E5E5] bg-[#FAFAF9] p-3 space-y-2">
                <div className="text-sm font-semibold text-[#2D2420]">Suggested FIFO Bags</div>
                {rawSuggestions.map((item) => (
                  <div key={item.lot_id} className="flex items-center justify-between text-sm text-[#5C4033]">
                    <span>{item.stock_ref_no} • {Number(item.move_bags_exact || item.move_bags || 0).toFixed(3)} bags</span>
                    <span>{Number(item.kg_moved || 0).toFixed(2)} kg</span>
                  </div>
                ))}
              </div>
            ) : null}
            <div>
              <Label>Note</Label>
              <Textarea value={transferForm.note} onChange={(e) => setTransferForm((prev) => ({ ...prev, note: e.target.value }))} className="border-[#E5E5E5]" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTransferModal(false)}>Cancel</Button>
            <Button onClick={handleTransferRawStock} disabled={saving}>{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Transfer'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showAdjustModal} onOpenChange={setShowAdjustModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[#2D2420]">Adjust Raw Stock</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Warehouse</Label>
              <Select value={adjustForm.warehouse_id} onValueChange={(value) => setAdjustForm((prev) => ({ ...prev, warehouse_id: value }))}>
                <SelectTrigger className="border-[#E5E5E5]"><SelectValue placeholder="Select warehouse" /></SelectTrigger>
                <SelectContent>{warehouseOptions.map((warehouse) => <SelectItem key={warehouse.id} value={warehouse.id}>{warehouse.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Raw Item</Label>
              <Select value={adjustForm.raw_item_id} onValueChange={(value) => setAdjustForm((prev) => ({ ...prev, raw_item_id: value }))}>
                <SelectTrigger className="border-[#E5E5E5]"><SelectValue placeholder="Select raw item" /></SelectTrigger>
                <SelectContent>{rawItemOptions.map((item) => <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>KG Adjustment</Label>
              <Input type="number" step="0.001" value={adjustForm.kg_adjustment} onChange={(e) => setAdjustForm((prev) => ({ ...prev, kg_adjustment: e.target.value }))} className="border-[#E5E5E5]" placeholder="Use negative value to reduce stock" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Bags Adjustment</Label><Input type="number" value={adjustForm.bags_adjustment} onChange={(e) => setAdjustForm((prev) => ({ ...prev, bags_adjustment: e.target.value }))} className="border-[#E5E5E5]" /></div>
              <div><Label>KG per Bag</Label><Input type="number" step="0.001" value={adjustForm.kg_per_bag} onChange={(e) => setAdjustForm((prev) => ({ ...prev, kg_per_bag: e.target.value }))} className="border-[#E5E5E5]" /></div>
            </div>
            <div className="text-xs text-[#A8A8A8]">For positive adjustments, enter KG per bag. For negative adjustments, KG value is enough.</div>
            <div>
              <Label>Note</Label>
              <Textarea value={adjustForm.note} onChange={(e) => setAdjustForm((prev) => ({ ...prev, note: e.target.value }))} className="border-[#E5E5E5]" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdjustModal(false)}>Cancel</Button>
            <Button onClick={handleAdjustRawStock} disabled={saving}>{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Adjust'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
